/**
 * Cart page (woocommerce/cart/cart.php): quantity stepper, removal with undo, discount code and adding
 * recommended products - all without reloading; the summary and the free delivery bar are re-rendered
 * by the server and swapped in as fragments.
 */
import {applyFragments, config, post, sprintf, storeCartFragments, toast, trackEvent} from './shared';

export const CartPage = () => {
    if (!document.querySelector('[data-wbs-cart]') || !config()) {
        return;
    }

    const {i18n} = config();
    const pending = {};

    const request = async (action, data, {focus = null} = {}) => {
        const cart = document.querySelector('[data-wbs-cart]');
        if (cart && cart.classList.contains('wbs-cart--empty')) {
            data.full = 1;
        }

        document.body.classList.add('wbs-cart-busy');

        try {
            const response = await post(action, {security: config().cartNonce, ...data});
            if (!response) {
                return null;
            }

            applyFragments(response.fragments);
            storeCartFragments(response.fragments, response.cart_hash);
            restoreFocus(focus);
            trackEvent(response.analytics);

            $(document.body).trigger('updated_cart_totals');

            return response;
        } catch (e) {
            toast(i18n.error, {type: 'error'});
            return null;
        } finally {
            document.body.classList.remove('wbs-cart-busy');
        }
    };

    const restoreFocus = (focus) => {
        if (!focus) {
            return;
        }
        const line = document.querySelector(`.wbs-line[data-key="${focus.key}"]`);
        const target = line && line.querySelector(focus.selector);
        if (target) {
            target.focus({preventScroll: true});
        }
    };

    const showNotices = (notices, fallbackToToast = true) => {
        (notices || []).forEach((notice) => {
            if (fallbackToToast) {
                toast(notice.message, {type: notice.type === 'error' ? 'error' : 'info'});
            }
        });
    };

    /* Quantity ---------------------------------------------------------------------------------- */

    const clamp = (input, value) => {
        const max = parseInt(input.getAttribute('max'), 10);
        let qty = Math.max(1, parseInt(value, 10) || 1);
        if (max > 0) {
            qty = Math.min(qty, max);
        }
        return qty;
    };

    const syncButtons = (input) => {
        const group = input.closest('.wbs-qty');
        const max = parseInt(input.getAttribute('max'), 10);
        const qty = parseInt(input.value, 10) || 1;
        group.querySelector('[data-step="-1"]').disabled = qty <= 1;
        group.querySelector('[data-step="1"]').disabled = max > 0 && qty >= max;
    };

    const sendQty = (key, qty, selector) => {
        clearTimeout(pending[key]);
        pending[key] = setTimeout(async () => {
            const response = await request('wbs_cart_qty', {key, qty}, {focus: {key, selector}});
            showNotices(response && response.notices);
        }, 400);
    };

    $(document).on('click', '.wbs-qty__btn', function () {
        const input = this.closest('.wbs-qty').querySelector('.wbs-qty__input');
        const qty = clamp(input, (parseInt(input.value, 10) || 1) + parseInt(this.dataset.step, 10));

        if (String(qty) === input.value) {
            return;
        }

        input.value = qty;
        syncButtons(input);
        sendQty(input.dataset.key, qty, `.wbs-qty__btn[data-step="${this.dataset.step}"]`);
    });

    $(document).on('change', '.wbs-qty__input', function () {
        const qty = clamp(this, this.value);
        this.value = qty;
        syncButtons(this);
        sendQty(this.dataset.key, qty, '.wbs-qty__input');
    });

    $(document).on('keydown', '.wbs-qty__input', function (event) {
        if (event.key === 'Enter') {
            event.preventDefault();
            $(this).trigger('change');
        }
    });

    /* Removal with undo ------------------------------------------------------------------------- */

    const restore = async (key, name) => {
        const response = await request('wbs_cart_restore', {key}, {focus: {key, selector: '.wbs-line__title a'}});
        if (response) {
            toast(sprintf(i18n.added, name));
        }
    };

    $(document).on('click', '[data-remove]', async function () {
        const line = this.closest('.wbs-line');
        if (line) {
            line.classList.add('is-removing');
        }

        const response = await request('wbs_cart_remove', {key: this.dataset.remove});

        if (response && response.removed) {
            const {key, name} = response.removed;
            // The removed row is gone - keep keyboard focus in the list instead of losing it to <body>.
            const list = document.querySelector('.wbs-card--items') || document.querySelector('[data-wbs-cart]');
            if (list) {
                list.setAttribute('tabindex', '-1');
                list.focus({preventScroll: true});
            }
            toast(sprintf(i18n.removed, name), {action: {label: i18n.undo, onClick: () => restore(key, name)}});
            $(document.body).trigger('removed_from_cart', [response.fragments, response.cart_hash]);
        } else if (line) {
            line.classList.remove('is-removing');
        }
    });

    /* Discount code ----------------------------------------------------------------------------- */

    $(document).on('click', '.wbs-coupon__toggle', function () {
        const panel = document.getElementById(this.getAttribute('aria-controls'));
        const open = this.getAttribute('aria-expanded') !== 'true';

        this.setAttribute('aria-expanded', open ? 'true' : 'false');
        panel.hidden = !open;

        if (open) {
            panel.querySelector('input').focus();
        }
    });

    const couponMessage = (notices) => {
        const message = document.getElementById('wbs-coupon-message');
        if (!message) {
            return;
        }
        const notice = (notices || [])[0];
        message.textContent = notice ? notice.message : '';
        // BEM modifiers, not "is-error": WooCommerce scripts remove every .is-error/.is-success element on the page.
        message.className = `wbs-coupon__message wbs-coupon__message--${notice && notice.type === 'error' ? 'error' : 'success'}`;
    };

    $(document).on('submit', '[data-coupon-form]', async function (event) {
        event.preventDefault();

        const input = this.querySelector('input[name="coupon_code"]');
        const button = this.querySelector('button[type="submit"]');

        button.disabled = true;
        const response = await request('wbs_cart_coupon', {code: input.value});
        button.disabled = false;

        if (response) {
            couponMessage(response.notices);
            const failed = (response.notices || []).some((notice) => notice.type === 'error');
            input.setAttribute('aria-invalid', failed ? 'true' : 'false');
            if (!failed) {
                input.value = '';
            }
        }
    });

    $(document).on('click', '[data-coupon-remove]', async function () {
        const response = await request('wbs_cart_coupon_remove', {code: this.dataset.couponRemove});
        if (response) {
            couponMessage(response.notices);
        }
    });

    /* Recommendations --------------------------------------------------------------------------- */

    $(document).on('click', '[data-add-to-cart]', async function () {
        this.disabled = true;
        this.classList.add('is-loading');

        const response = await request('wbs_cart_add', {product_id: this.dataset.addToCart});

        if (response && response.added) {
            toast(sprintf(i18n.added, response.added.name));
        } else {
            showNotices(response && response.notices);
            this.disabled = false;
            this.classList.remove('is-loading');
        }
    });
};

/**
 * "Kontynuuj zakupy" returns to the last viewed category - remember it while browsing.
 */
export const rememberCategory = () => {
    const body = document.body.classList;

    if (body.contains('tax-product_cat') || body.contains('post-type-archive-product')) {
        const url = window.location.href.split('#')[0];
        document.cookie = `wbs_last_category=${encodeURIComponent(url)}; path=/; max-age=${30 * 24 * 3600}; SameSite=Lax`;
    }
};
